import { Button, CircularProgress, Stack } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import { useEventStore } from '@/state/stores/use-event-store';
import { getOriginEventId } from '@/helpers/event.helper';
import { useAssignRemoteSpeakerQuotaMutation } from '@/state/mutations/contributors/use-configure-remote-speaker';
import { useNotifications } from '@/hooks/use-notifications';
import { useInsufficientRemoteSpeakerDaysInfo } from '@/hooks/use-insufficient-remote-speaker-days-info';
import { PRODUCT_KEYS } from '@/types/dtos/orders/response/product.dto';
import { useProductsStore } from '@/state/stores/use-products-store';
import { useGetEventOrdersQuery } from '@/state/queries/orders/use-get-event-orders';
import EventCatalogOrderCheckoutModal from '@/components/events/event-catalog/event-catalog-order-checkout.modal';
import EventCatalogProductDetails from '@/components/events/event-catalog/event-catalog-product-details';
import { useQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import {
  findQuotaBalanceForScopedProductContext,
  resolveScopedProductBalanceContext,
} from '@/helpers/product-quota.helper';
import { clampRemoteSpeakerDayCheckoutQuantity } from '@/utils/remote-speaker-status.utils';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { resolveEventPermissionActions } from '@/utils/event-crew-permissions';
import Modal from '@/components/common/modal';
import { useBreakpointUp } from '@/hooks/use-breakpoint';

export type RemoteSpeakerDayEntitlementModalProps = {
  open: boolean;
  onClose: () => void;
  contributor: ContributorDto;
  dayIds: string[];
  dayLabel: string;
  isEventManager: boolean;
};

type EntitlementModalView = 'assign' | 'purchase';

export function RemoteSpeakerDayEntitlementModal({
  open,
  onClose,
  contributor,
  dayIds,
  dayLabel,
  isEventManager,
}: RemoteSpeakerDayEntitlementModalProps) {
  const { t } = useTranslation();
  const notification = useNotifications();
  const isWideScreen = useBreakpointUp('sm');
  const event = useEventStore((s) => s.event);
  const eventPlan = useEventStore((s) => s.eventPlan);
  const eventForContext = eventPlan ?? event;
  const originEventId = event ? getOriginEventId(event) : undefined;
  const displayName = contributor.profiles[0]?.displayName ?? t('event.detail.contributors.unknownContributor');

  const catalogProducts = useProductsStore((s) => s.products);
  const { data: orders } = useGetEventOrdersQuery(originEventId);
  const { data: permissions } = usePermissionsQuery();
  const eventPermissions = useMemo(
    () => resolveEventPermissionActions(permissions, eventForContext ?? event),
    [permissions, eventForContext, event],
  );
  const canAddToCheckout = eventPermissions.includes('create_order');

  const { mutateAsync: assignRemoteSpeakerQuota, isPending: isAssignPending } = useAssignRemoteSpeakerQuotaMutation();
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [modalView, setModalView] = useState<EntitlementModalView | null>(null);
  const prevOpenRef = useRef(false);

  const insufficientInfo = useInsufficientRemoteSpeakerDaysInfo(eventForContext, contributor, dayIds);
  const { data: balances } = useQuotaBalances('Event', originEventId);

  const availableCredits = useMemo(() => {
    const balanceCtx = resolveScopedProductBalanceContext(catalogProducts, PRODUCT_KEYS.EVENT_REMOTE_SPEAKER_DAY);
    if (!balanceCtx) return 0;
    return findQuotaBalanceForScopedProductContext(balances, balanceCtx)?.available ?? 0;
  }, [catalogProducts, balances]);

  const canAssignFromEntitlements = dayIds.length > 0 && availableCredits >= dayIds.length;
  const needsPurchase = insufficientInfo !== null;

  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    if (justOpened) {
      setModalView(canAssignFromEntitlements ? 'assign' : 'purchase');
    }
    if (!open) {
      setModalView(null);
    }
    prevOpenRef.current = open;
  }, [open, canAssignFromEntitlements]);

  /** Keep assign UI while allocating; avoid purchase flash when balances update before contributor refetch. */
  const effectiveView: EntitlementModalView =
    isAssignPending || modalView === 'assign' ? 'assign' : (modalView ?? (needsPurchase ? 'purchase' : 'assign'));
  const showPurchaseOptions = effectiveView === 'purchase';
  const showAssignAction = effectiveView === 'assign' && (canAssignFromEntitlements || isAssignPending);

  const speakerPurchaseQuantity = useMemo(() => {
    if (!insufficientInfo) return 1;
    return clampRemoteSpeakerDayCheckoutQuantity(
      insufficientInfo.neededCredits,
      eventForContext?.days?.length ?? 0,
      contributor,
    );
  }, [insufficientInfo, eventForContext?.days?.length, contributor]);

  const eventPurchaseQuantity = useMemo(() => {
    if (!insufficientInfo) return 1;
    return clampRemoteSpeakerDayCheckoutQuantity(
      insufficientInfo.neededCredits,
      eventForContext?.days?.length ?? 0,
      undefined,
    );
  }, [insufficientInfo, eventForContext?.days?.length]);

  const handleCheckoutRequested = useCallback(() => {
    onClose();
    setCheckoutOpen(true);
  }, [onClose, setCheckoutOpen]);

  const handleAssign = async () => {
    if (!originEventId || dayIds.length === 0) return;
    try {
      await assignRemoteSpeakerQuota({
        eventId: originEventId,
        contributorId: contributor.id,
        dayIds,
      });
      notification.notifySuccess(
        t('event.detail.contributors.remoteSpeaker.assignQuotaSuccess', { name: displayName }),
      );
      onClose();
    } catch {
      notification.notifyError(t('event.detail.contributors.remoteSpeaker.assignQuotaError'));
    }
  };

  const modalTitle = isEventManager
    ? t('event.detail.contributors.remoteSpeaker.dayEntitlementTitle', { name: displayName })
    : t('event.detail.contributors.remoteSpeaker.dayEntitlementContactTitle');

  const modalDescription = isEventManager
    ? showPurchaseOptions
      ? t('event.detail.contributors.remoteSpeaker.dayEntitlementDescriptionPurchase', { name: displayName })
      : t('event.detail.contributors.remoteSpeaker.dayEntitlementDescriptionAssign', { name: displayName })
    : t('event.detail.contributors.remoteSpeaker.dayEntitlementContactDescription', {
        name: displayName,
        dayLabel,
      });

  return (
    <>
      <Modal
        open={open}
        size={isWideScreen ? 'md' : 'sm'}
        handleClose={onClose}
        isolateClickPropagation
        title={modalTitle}
        description={modalDescription}
        footer={
          <>
            <Button onClick={onClose} variant="outlined">
              {isEventManager ? t('common.cancel') : t('common.close')}
            </Button>
            {isEventManager && showAssignAction ? (
              <Button variant="contained" onClick={handleAssign} disabled={isAssignPending}>
                {isAssignPending ? (
                  <CircularProgress size={20} />
                ) : (
                  t('event.detail.contributors.remoteSpeaker.dayEntitlementAssignAction')
                )}
              </Button>
            ) : null}
          </>
        }
      >
        {isEventManager && showPurchaseOptions ? (
          <Stack spacing={2} sx={{ pt: 1 }}>
            <EventCatalogProductDetails
              productKey={PRODUCT_KEYS.EVENT_REMOTE_SPEAKER_DAY}
              event={eventForContext ?? undefined}
              orders={orders}
              expanded
              readonly={!canAddToCheckout}
              showPrice
              contributorId={contributor.id}
              quantity={speakerPurchaseQuantity}
              titleOverride={t('event.detail.contributors.remoteSpeaker.dayEntitlementScopedOfferTitle', {
                name: displayName,
              })}
              subtitleOverride={t('event.detail.contributors.remoteSpeaker.dayEntitlementScopedOfferSubtitle')}
              detailsOverride={t('event.detail.contributors.remoteSpeaker.dayEntitlementScopedOfferDetails', {
                name: displayName,
              })}
              onCheckoutRequested={handleCheckoutRequested}
            />
            <EventCatalogProductDetails
              productKey={PRODUCT_KEYS.EVENT_REMOTE_SPEAKER_DAY}
              event={eventForContext ?? undefined}
              orders={orders}
              readonly={!canAddToCheckout}
              showPrice
              quantity={eventPurchaseQuantity}
              titleOverride={t('event.detail.contributors.remoteSpeaker.dayEntitlementEventOfferTitle')}
              subtitleOverride={t('event.detail.contributors.remoteSpeaker.dayEntitlementEventOfferSubtitle')}
              detailsOverride={t('event.detail.contributors.remoteSpeaker.dayEntitlementEventOfferDetails')}
              onCheckoutRequested={handleCheckoutRequested}
            />
          </Stack>
        ) : null}
      </Modal>
      <EventCatalogOrderCheckoutModal
        open={checkoutOpen}
        event={eventForContext ?? undefined}
        onModalClose={() => setCheckoutOpen(false)}
      />
    </>
  );
}

function resolveRemoteSpeakerDayLabel(
  dayId: string | undefined,
  event: { days?: Array<{ id: string }> } | undefined,
  t: (key: string, options?: Record<string, string | number> & { defaultValue?: string }) => string,
): string {
  if (!dayId || !event?.days?.length) {
    return t('event.detail.contributors.remoteSpeaker.dayLabel', { number: 1, defaultValue: 'Day 1' });
  }
  const index = event.days.findIndex((day) => day.id === dayId);
  const dayNumber = index >= 0 ? index + 1 : 1;
  return t('event.detail.contributors.remoteSpeaker.dayLabel', {
    number: dayNumber,
    defaultValue: `Day ${dayNumber}`,
  });
}

export function useRemoteSpeakerDayEntitlementModalState() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [contributor, setContributor] = useState<ContributorDto | undefined>();
  const [dayIds, setDayIds] = useState<string[]>([]);
  const [dayLabel, setDayLabel] = useState('');

  const openForContributorDay = (
    nextContributor: ContributorDto,
    dayId: string | undefined,
    event: { days?: Array<{ id: string }> } | undefined,
  ) => {
    setContributor(nextContributor);
    setDayIds(dayId ? [dayId] : []);
    setDayLabel(resolveRemoteSpeakerDayLabel(dayId, event, t));
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setContributor(undefined);
    setDayIds([]);
    setDayLabel('');
  };

  return {
    open,
    contributor,
    dayIds,
    dayLabel,
    openForContributorDay,
    close,
  };
}
